import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { FaArrowLeft, FaAtom, FaLightbulb, FaCalculator, FaCog, FaFlask, FaMicroscope, FaRocket, FaEye, FaBalanceScale, FaTachometerAlt, FaBolt, FaPalette, FaCompass, FaMagnet, FaShieldAlt, FaThermometerHalf, FaHandPaper, FaRunning, FaCircle, FaDna, FaGlobe } from 'react-icons/fa';
import '../Lab/Lab.css';

const defaultConcepts = {
  'projectile-motion-lab': 'حركة المقذوفات، المسار المكافئ، السرعة، الزاوية',
  'states-of-matter-basics-lab': 'حالات المادة، الصلب، السائل، الغاز، التحولات',
  'states-of-matter-lab': 'حالات المادة، التحولات، درجة الحرارة، الضغط',
  'gravity-and-orbits-lab': 'الجاذبية، المدارات، الكواكب، السرعة المدارية',
  'charges-and-fields-lab': 'الشحنات الكهربائية، المجالات الكهربائية، القوى',
  'rutherford-scattering-lab': 'تشتت الجسيمات، النواة الذرية، نموذج رذرفورد',
  'bending-light-lab': 'انكسار الضوء، انعكاس الضوء، العدسات، المرايا',
  'hookes-law-lab': 'قانون هوك، المرونة، الزنبركات، القوة الاسترجاعية',
  'energy-skate-park-lab': 'الطاقة الحركية، الطاقة الوضعية، تحولات الطاقة',
  'faradays-law-lab': 'الحث الكهرومغناطيسي، قانون فاراداي، المجال المغناطيسي',
  'wave-on-a-string-lab': 'الموجات الميكانيكية، التردد، السعة، الطول الموجي',
  'color-vision-lab': 'الضوء، الألوان، الرؤية، الطيف المرئي',
  'balancing-act-lab': 'الاتزان، العزوم، مركز الثقل، القوى',
  'under-pressure-lab': 'الضغط، الغازات، قانون بويل، درجة الحرارة',
  'friction-lab': 'الاحتكاك، القوى، الحركة، المعاملات',
  'forces-and-motion-basics-lab': 'القوى، الحركة، قانون نيوتن، التسارع',
  'john-travoltage-lab': 'الكهرباء الساكنة، الشحنات، التفريغ الكهربائي',
  'gravity-force-lab': 'قوة الجذب، قانون الجذب العام، الكتلة، المسافة',
  'balloons-and-static-electricity-lab': 'الكهرباء الساكنة، الشحنات، البالونات',
  'ohms-law-lab': 'قانون أوم، الجهد، التيار، المقاومة',
  'resistance-in-a-wire-lab': 'المقاومة، الموصلات، الطول، المساحة المقطعية',
  'build-an-atom-lab': 'الذرة، البروتونات، النيوترونات، الإلكترونات، العناصر',
  // معامل الرياضيات
  'vector-addition-lab': 'المتجهات، الجمع المتجهي، المكونات، الاتجاه',
  'masses-and-springs-lab': 'الحركة التوافقية، الزنبركات، التردد، السعة',
  'pendulum-lab': 'البندول البسيط، التردد، الطول، الجاذبية',
  'projectile-motion-math-lab': 'حركة المقذوفات، المسار المكافئ، السرعة، الزاوية',
  'wave-on-a-string-math-lab': 'الموجات، التردد، السعة، الطول الموجي',
  'balancing-act-math-lab': 'الاتزان، العزوم، مركز الثقل، القوى',
  'ohms-law-math-lab': 'قانون أوم، الجهد، التيار، المقاومة',
  'resistance-in-a-wire-math-lab': 'المقاومة، الموصلات، الطول، المساحة المقطعية',
  // معامل الكيمياء
  'build-a-nucleus-lab': 'النواة، البروتونات، النيوترونات، النظائر',
  'density-lab': 'الكثافة، الكتلة، الحجم، الكثافة النسبية',
  'build-a-molecule-lab': 'الجزيئات، الروابط، الذرات، المركبات',
  'diffusion-lab': 'الانتشار، الجزيئات، الحركة العشوائية، التركيز',
  'gas-properties-lab': 'الغازات، الضغط، الحجم، درجة الحرارة، قانون بويل',
  'blackbody-spectrum-lab': 'إشعاع الجسم الأسود، الطيف، الطاقة، درجة الحرارة',
  'energy-forms-and-changes-lab': 'الطاقة، التحولات، الحرارة، العمل',
  'coulombs-law-lab': 'قانون كولوم، الشحنات، القوى الكهربائية، المسافة',
  'molecule-polarity-lab': 'قطبية الجزيئات، الروابط، الشحنات الجزئية',
  'states-of-matter-basics-chem-lab': 'حالات المادة، الصلب، السائل، الغاز',
  'states-of-matter-chem-lab': 'حالات المادة، التحولات، درجة الحرارة، الضغط',
  'rutherford-scattering-chem-lab': 'تشتت رذرفورد، النواة، الجسيمات ألفا',
  'isotopes-and-atomic-mass-lab': 'النظائر، الكتلة الذرية، البروتونات، النيوترونات',
  'molecule-shapes-lab': 'أشكال الجزيئات، الهندسة الجزيئية، الروابط',
  'molecule-shapes-basics-lab': 'أشكال الجزيئات، الروابط، الزوايا',
  'reactants-products-and-leftovers-lab': 'التفاعلات الكيميائية، المتفاعلات، النواتج',
  'ph-scale-basics-lab': 'مقياس pH، الأحماض، القواعد، الرقم الهيدروجيني',
  'wave-on-a-string-chem-lab': 'الموجات، التردد، السعة، الطول الموجي',
  'ph-scale-lab': 'مقياس pH، الأحماض، القواعد، التركيز',
  'balancing-chemical-equations-lab': 'موازنة المعادلات، المعاملات، المتفاعلات، النواتج',
  'acid-base-solutions-lab': 'الأحماض، القواعد، المحاليل، التعادل',
  'concentration-lab': 'التركيز، المحاليل، المذاب، المذيب',
  'balloons-and-static-electricity-chem-lab': 'الكهرباء الساكنة، الشحنات، البالونات',
  'beers-law-lab': 'قانون بير، الامتصاص، التركيز، الطول الموجي',
  'molarity-lab': 'المولارية، التركيز المولي، المول، الحجم',
  'build-an-atom-chem-lab': 'الذرة، البروتونات، النيوترونات، الإلكترونات',
  // معامل الأحياء
  'density-bio-lab': 'الكثافة، الكتلة، الحجم، الكثافة النسبية',
  'natural-selection-lab': 'الانتقاء الطبيعي، التطور، البقاء للأصلح، الطفرات',
  'gene-expression-essentials-lab': 'التعبير الجيني، البروتينات، الحمض النووي، الجينات',
  'molecule-polarity-bio-lab': 'قطبية الجزيئات، الروابط، الشحنات الجزئية',
  'neuron-lab': 'الخلايا العصبية، الإشارات العصبية، النواقل العصبية',
  'color-vision-bio-lab': 'رؤية الألوان، العين، المخاريط، العصي',
  'ph-scale-bio-lab': 'مقياس pH، الأحماض، القواعد، التركيز',
  // معامل علم الأرض
  'greenhouse-effect-lab': 'ظاهرة البيت الزجاجي، المناخ، الاحتباس الحراري، الغازات',
  'density-earth-lab': 'الكثافة، الكتلة، الحجم، الكثافة النسبية',
  'gravity-force-lab-basics-lab': 'الجاذبية، القوى، الكتلة، المسافة',
  'waves-intro-lab': 'الموجات، التردد، السعة، الطول الموجي',
  'diffusion-earth-lab': 'الانتشار، الجزيئات، الحركة العشوائية، التركيز',
  'gas-properties-earth-lab': 'الغازات، الضغط، الحجم، درجة الحرارة، قانون بويل',
  'blackbody-spectrum-earth-lab': 'إشعاع الجسم الأسود، الطيف، الطاقة، درجة الحرارة',
  'wave-interference-lab': 'تداخل الموجات، الحيود، التردد، السعة',
  'gravity-and-orbits-earth-lab': 'الجاذبية، المدارات، الكواكب، السرعة المدارية',
  'wave-on-a-string-earth-lab': 'الموجات الميكانيكية، التردد، السعة، الطول الموجي',
  'ph-scale-earth-lab': 'مقياس pH، الأحماض، القواعد، التركيز',
  'under-pressure-earth-lab': 'الضغط، الغازات، قانون بويل، درجة الحرارة',
  'gravity-force-lab-earth-lab': 'قوة الجذب، قانون الجذب العام، الكتلة، المسافة',
  'balloons-and-static-electricity-earth-lab': 'الكهرباء الساكنة، الشحنات، البالونات',
};

const defaultTime = '15-30 دقيقة';
const defaultLevel = 'الثانوية العامة';

const defaultInstructions = [
  {
    title: 'استكشاف المحاكاة',
    desc: 'قم بتغيير الإعدادات أو القيم في المحاكاة ولاحظ كيف تتغير النتائج.'
  },
  {
    title: 'تجربة سيناريوهات مختلفة',
    desc: 'جرب سيناريوهات أو حالات مختلفة لفهم المفهوم العلمي بشكل أعمق.'
  },
  {
    title: 'ملاحظة العلاقات',
    desc: 'لاحظ كيف تؤثر المتغيرات على بعضها البعض داخل المحاكاة.'
  }
];

const labsData = {
  'projectile-motion-lab': {
    title: 'حركة المقذوفات',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaRocket />, 
    description: 'تجربة تفاعلية في حركة المقذوفات والمسار المكافئ',
    iframe: 'https://phet.colorado.edu/sims/html/projectile-motion/latest/projectile-motion_ar_SA.html'
  },
  'states-of-matter-basics-lab': {
    title: 'أساسيات حالات المادة',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaFlask />, 
    description: 'تجربة في حالات المادة الأساسية (صلب، سائل، غاز)',
    iframe: 'https://phet.colorado.edu/sims/html/states-of-matter-basics/latest/states-of-matter-basics_ar_SA.html'
  },
  'states-of-matter-lab': {
    title: 'حالات المادة',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaThermometerHalf />, 
    description: 'تجربة متقدمة في حالات المادة والتحولات',
    iframe: 'https://phet.colorado.edu/sims/html/states-of-matter/latest/states-of-matter_ar_SA.html'
  },
  'gravity-and-orbits-lab': {
    title: 'الجاذبية والمدارات',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaCompass />, 
    description: 'تجربة في الجاذبية والمدارات الكوكبية',
    iframe: 'https://phet.colorado.edu/sims/html/gravity-and-orbits/latest/gravity-and-orbits_ar_SA.html'
  },
  'charges-and-fields-lab': {
    title: 'الشحنات والمجالات',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaBolt />, 
    description: 'تجربة في الشحنات الكهربائية والمجالات',
    iframe: 'https://phet.colorado.edu/sims/html/charges-and-fields/latest/charges-and-fields_ar_SA.html'
  },
  'rutherford-scattering-lab': {
    title: 'تشتت رذرفورد',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaAtom />, 
    description: 'تجربة في تشتت الجسيمات والنواة الذرية',
    iframe: 'https://phet.colorado.edu/sims/html/rutherford-scattering/latest/rutherford-scattering_ar_SA.html'
  },
  'bending-light-lab': {
    title: 'انحناء الضوء',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaEye />, 
    description: 'تجربة في انكسار وانعكاس الضوء',
    iframe: 'https://phet.colorado.edu/sims/html/bending-light/latest/bending-light_ar_SA.html'
  },
  'hookes-law-lab': {
    title: 'قانون هوك',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaCog />, 
    description: 'تجربة في المرونة وقانون هوك',
    iframe: 'https://phet.colorado.edu/sims/html/hookes-law/latest/hookes-law_ar_SA.html'
  },
  'energy-skate-park-lab': {
    title: 'Energy Skate Park',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaRunning />, 
    description: 'تجربة في تحولات الطاقة الحركية والوضعية',
    iframe: 'https://phet.colorado.edu/sims/html/energy-skate-park-basics/latest/energy-skate-park-basics_ar_SA.html'
  },
  'faradays-law-lab': {
    title: 'قانون فاراداي',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaMagnet />, 
    description: 'تجربة في الحث الكهرومغناطيسي',
    iframe: 'https://phet.colorado.edu/sims/html/faradays-law/latest/faradays-law_ar_SA.html'
  },
  'wave-on-a-string-lab': {
    title: 'موجة على وتر',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaCircle />, 
    description: 'تجربة في الموجات الميكانيكية على الأوتار',
    iframe: 'https://phet.colorado.edu/sims/html/wave-on-a-string/latest/wave-on-a-string_ar_SA.html'
  },
  'color-vision-lab': {
    title: 'رؤية الألوان',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaPalette />, 
    description: 'تجربة في الضوء والألوان والرؤية',
    iframe: 'https://phet.colorado.edu/sims/html/color-vision/latest/color-vision_ar_SA.html'
  },
  'balancing-act-lab': {
    title: 'تمثيل الاتزان',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaBalanceScale />, 
    description: 'تجربة في الاتزان والعزوم',
    iframe: 'https://phet.colorado.edu/sims/html/balancing-act/latest/balancing-act_ar_SA.html'
  },
  'under-pressure-lab': {
    title: 'الضغط',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaTachometerAlt />, 
    description: 'تجربة في الضغط وتأثيره على المادة',
    iframe: 'https://phet.colorado.edu/sims/html/under-pressure/latest/under-pressure_ar_SA.html'
  },
  'friction-lab': {
    title: 'الاحتكاك',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaHandPaper />, 
    description: 'تجربة في قوى الاحتكاك وتأثيرها على الحركة',
    iframe: 'https://phet.colorado.edu/sims/html/friction/latest/friction_ar_SA.html'
  },
  'forces-and-motion-basics-lab': {
    title: 'مبادئ القوة والحركة',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaRunning />, 
    description: 'تجربة أساسية في القوى والحركة',
    iframe: 'https://phet.colorado.edu/sims/html/forces-and-motion-basics/latest/forces-and-motion-basics_ar_SA.html'
  },
  'john-travoltage-lab': {
    title: 'جون ترافولتاج',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaBolt />, 
    description: 'تجربة تفاعلية في الكهرباء الساكنة',
    iframe: 'https://phet.colorado.edu/sims/html/john-travoltage/latest/john-travoltage_ar_SA.html'
  },
  'gravity-force-lab': {
    title: 'معمل قوة الجذب',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaCompass />, 
    description: 'تجربة في قوى الجذب بين الأجسام',
    iframe: 'https://phet.colorado.edu/sims/html/gravity-force-lab/latest/gravity-force-lab_ar_SA.html'
  },
  'balloons-and-static-electricity-lab': {
    title: 'البالونات والكهرباء الساكنة',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaCircle />, 
    description: 'تجربة في الكهرباء الساكنة والبالونات',
    iframe: 'https://phet.colorado.edu/sims/html/balloons-and-static-electricity/latest/balloons-and-static-electricity_ar_SA.html'
  },
  'ohms-law-lab': {
    title: 'قانون أوم',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaBolt />, 
    description: 'تجربة في العلاقة بين الجهد والتيار والمقاومة',
    iframe: 'https://phet.colorado.edu/sims/html/ohms-law/latest/ohms-law_ar_SA.html'
  },
  'resistance-in-a-wire-lab': {
    title: 'مقاومة سلك',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaBolt />, 
    description: 'تجربة في مقاومة الموصلات الكهربائية',
    iframe: 'https://phet.colorado.edu/sims/html/resistance-in-a-wire/latest/resistance-in-a-wire_ar_SA.html'
  },
  'build-an-atom-lab': {
    title: 'بناء ذرة',
    subject: 'الفيزياء',
    subjectColor: '#e74c3c',
    icon: <FaAtom />, 
    description: 'تجربة تفاعلية في بناء الذرات والعناصر',
    iframe: 'https://phet.colorado.edu/sims/html/build-an-atom/latest/build-an-atom_ar_SA.html'
  },
  // معامل الرياضيات
  'vector-addition-lab': {
    title: 'إضافة المتجهات',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة تفاعلية في جمع المتجهات وتمثيلها',
    iframe: 'https://phet.colorado.edu/sims/html/vector-addition/latest/vector-addition_ar_SA.html'
  },
  'masses-and-springs-lab': {
    title: 'الكتل والزنبركات',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في الحركة التوافقية البسيطة',
    iframe: 'https://phet.colorado.edu/sims/html/masses-and-springs/latest/masses-and-springs_ar_SA.html'
  },
  'pendulum-lab': {
    title: 'معمل البندول',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حركة البندول البسيط',
    iframe: 'https://phet.colorado.edu/sims/html/pendulum-lab/latest/pendulum-lab_ar_SA.html'
  },
  'projectile-motion-math-lab': {
    title: 'حركة المقذوفات',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حساب مسار المقذوفات',
    iframe: 'https://phet.colorado.edu/sims/html/projectile-motion/latest/projectile-motion_ar_SA.html'
  },
  'wave-on-a-string-math-lab': {
    title: 'موجة على وتر',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حساب خصائص الموجات',
    iframe: 'https://phet.colorado.edu/sims/html/wave-on-a-string/latest/wave-on-a-string_ar_SA.html'
  },
  'balancing-act-math-lab': {
    title: 'تمثيل الاتزان',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حساب العزوم والاتزان',
    iframe: 'https://phet.colorado.edu/sims/html/balancing-act/latest/balancing-act_ar_SA.html'
  },
  'ohms-law-math-lab': {
    title: 'قانون أوم',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حساب المقاومة والجهد والتيار',
    iframe: 'https://phet.colorado.edu/sims/html/ohms-law/latest/ohms-law_ar_SA.html'
  },
  'resistance-in-a-wire-math-lab': {
    title: 'مقاومة سلك',
    subject: 'الرياضيات',
    subjectColor: '#9b59b6',
    icon: <FaCalculator />, 
    description: 'تجربة في حساب مقاومة الموصلات',
    iframe: 'https://phet.colorado.edu/sims/html/resistance-in-a-wire/latest/resistance-in-a-wire_ar_SA.html'
  },
  // معامل الكيمياء
  'build-a-nucleus-lab': {
    title: 'بناء نواة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة تفاعلية في بناء النواة الذرية',
    iframe: 'https://phet.colorado.edu/sims/html/build-a-nucleus/latest/build-a-nucleus_ar_SA.html'
  },
  'density-lab': {
    title: 'الكثافة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في قياس الكثافة والكتلة والحجم',
    iframe: 'https://phet.colorado.edu/sims/html/density/latest/density_ar_SA.html'
  },
  'build-a-molecule-lab': {
    title: 'بناء الجزيء',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة تفاعلية في بناء الجزيئات',
    iframe: 'https://phet.colorado.edu/sims/html/build-a-molecule/latest/build-a-molecule_ar_SA.html'
  },
  'diffusion-lab': {
    title: 'الانتشار',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في عملية الانتشار الجزيئي',
    iframe: 'https://phet.colorado.edu/sims/html/diffusion/latest/diffusion_ar_SA.html'
  },
  'gas-properties-lab': {
    title: 'خصائص الغاز',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في خصائص الغازات وقوانينها',
    iframe: 'https://phet.colorado.edu/sims/html/gas-properties/latest/gas-properties_ar_SA.html'
  },
  'blackbody-spectrum-lab': {
    title: 'إشعاع الجسم الأسود',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة في إشعاع الجسم الأسود والطيف',
    iframe: 'https://phet.colorado.edu/sims/html/blackbody-spectrum/latest/blackbody-spectrum_ar_SA.html'
  },
  'energy-forms-and-changes-lab': {
    title: 'أشكال الطاقة وتحولاتها',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaCalculator />, 
    description: 'تجربة في تحولات الطاقة المختلفة',
    iframe: 'https://phet.colorado.edu/sims/html/energy-forms-and-changes/latest/energy-forms-and-changes_ar_SA.html'
  },
  'coulombs-law-lab': {
    title: 'قانون كولوم',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaBolt />, 
    description: 'تجربة في قوى التجاذب والتنافر الكهربائي',
    iframe: 'https://phet.colorado.edu/sims/html/coulombs-law/latest/coulombs-law_ar_SA.html'
  },
  'molecule-polarity-lab': {
    title: 'قطبية الجزيء',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة في قطبية الجزيئات والروابط',
    iframe: 'https://phet.colorado.edu/sims/html/molecule-polarity/latest/molecule-polarity_ar_SA.html'
  },
  'states-of-matter-basics-chem-lab': {
    title: 'أساسيات حالات المادة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في حالات المادة الأساسية',
    iframe: 'https://phet.colorado.edu/sims/html/states-of-matter-basics/latest/states-of-matter-basics_ar_SA.html'
  },
  'states-of-matter-chem-lab': {
    title: 'حالات المادة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة متقدمة في حالات المادة والتحولات',
    iframe: 'https://phet.colorado.edu/sims/html/states-of-matter/latest/states-of-matter_ar_SA.html'
  },
  'rutherford-scattering-chem-lab': {
    title: 'تشتت رذرفورد',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة في تشتت الجسيمات والنواة الذرية',
    iframe: 'https://phet.colorado.edu/sims/html/rutherford-scattering/latest/rutherford-scattering_ar_SA.html'
  },
  'isotopes-and-atomic-mass-lab': {
    title: 'النظائر والكتلة الذرية',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة في النظائر والكتل الذرية',
    iframe: 'https://phet.colorado.edu/sims/html/isotopes-and-atomic-mass/latest/isotopes-and-atomic-mass_ar_SA.html'
  },
  'molecule-shapes-lab': {
    title: 'أشكال الجزيئات',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة في أشكال الجزيئات والهندسة الجزيئية',
    iframe: 'https://phet.colorado.edu/sims/html/molecule-shapes/latest/molecule-shapes_ar_SA.html'
  },
  'molecule-shapes-basics-lab': {
    title: 'أشكال الجزيء الأساسية',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة أساسية في أشكال الجزيئات',
    iframe: 'https://phet.colorado.edu/sims/html/molecule-shapes-basics/latest/molecule-shapes-basics_ar_SA.html'
  },
  'reactants-products-and-leftovers-lab': {
    title: 'المتفاعلات والنواتج والمخلفات',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في التفاعلات الكيميائية',
    iframe: 'https://phet.colorado.edu/sims/html/reactants-products-and-leftovers/latest/reactants-products-and-leftovers_ar_SA.html'
  },
  'ph-scale-basics-lab': {
    title: 'مقياس الرقم الهيدروجيني',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة أساسية في مقياس pH',
    iframe: 'https://phet.colorado.edu/sims/html/ph-scale-basics/latest/ph-scale-basics_ar_SA.html'
  },
  'wave-on-a-string-chem-lab': {
    title: 'موجة على وتر',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaCircle />, 
    description: 'تجربة في الموجات الميكانيكية',
    iframe: 'https://phet.colorado.edu/sims/html/wave-on-a-string/latest/wave-on-a-string_ar_SA.html'
  },
  'ph-scale-lab': {
    title: 'مقياس pH',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة متقدمة في مقياس الرقم الهيدروجيني',
    iframe: 'https://phet.colorado.edu/sims/html/ph-scale/latest/ph-scale_ar_SA.html'
  },
  'balancing-chemical-equations-lab': {
    title: 'وزن المعادلات الكيميائية',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaCalculator />, 
    description: 'تجربة في موازنة المعادلات الكيميائية',
    iframe: 'https://phet.colorado.edu/sims/html/balancing-chemical-equations/latest/balancing-chemical-equations_ar_SA.html'
  },
  'acid-base-solutions-lab': {
    title: 'محاليل الحمض والقاعدة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في الأحماض والقواعد',
    iframe: 'https://phet.colorado.edu/sims/html/acid-base-solutions/latest/acid-base-solutions_ar_SA.html'
  },
  'concentration-lab': {
    title: 'التركيز',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في تركيز المحاليل',
    iframe: 'https://phet.colorado.edu/sims/html/concentration/latest/concentration_ar_SA.html'
  },
  'balloons-and-static-electricity-chem-lab': {
    title: 'البالونات والكهرباء الساكنة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaBolt />, 
    description: 'تجربة في الكهرباء الساكنة',
    iframe: 'https://phet.colorado.edu/sims/html/balloons-and-static-electricity/latest/balloons-and-static-electricity_ar_SA.html'
  },
  'beers-law-lab': {
    title: 'قانون بير',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaFlask />, 
    description: 'تجربة في قانون بير والامتصاص',
    iframe: 'https://phet.colorado.edu/sims/html/beers-law-lab/latest/beers-law-lab_ar_SA.html'
  },
  'molarity-lab': {
    title: 'المولارية',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaCalculator />, 
    description: 'تجربة في المولارية والتركيز المولي',
    iframe: 'https://phet.colorado.edu/sims/html/molarity/latest/molarity_ar_SA.html'
  },
  'build-an-atom-chem-lab': {
    title: 'بناء ذرة',
    subject: 'الكيمياء',
    subjectColor: '#3498db',
    icon: <FaAtom />, 
    description: 'تجربة تفاعلية في بناء الذرات',
    iframe: 'https://phet.colorado.edu/sims/html/build-an-atom/latest/build-an-atom_ar_SA.html'
  },
  // معامل الأحياء
  'density-bio-lab': {
    title: 'الكثافة',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaFlask />, 
    description: 'تجربة في قياس الكثافة والكتلة والحجم',
    iframe: 'https://phet.colorado.edu/sims/html/density/latest/density_ar_SA.html'
  },
  'natural-selection-lab': {
    title: 'الانتقاء الطبيعي',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaDna />, 
    description: 'تجربة في آلية التطور والانتقاء الطبيعي',
    iframe: 'https://phet.colorado.edu/sims/html/natural-selection/latest/natural-selection_ar_SA.html'
  },
  'gene-expression-essentials-lab': {
    title: 'أساسيات التعبير الجيني',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaDna />, 
    description: 'تجربة في التعبير الجيني والبروتينات',
    iframe: 'https://phet.colorado.edu/sims/html/gene-expression-essentials/latest/gene-expression-essentials_ar_SA.html'
  },
  'molecule-polarity-bio-lab': {
    title: 'قطبية الجزيء',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaAtom />, 
    description: 'تجربة في قطبية الجزيئات والروابط',
    iframe: 'https://phet.colorado.edu/sims/html/molecule-polarity/latest/molecule-polarity_ar_SA.html'
  },
  'neuron-lab': {
    title: 'العصبون',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaMicroscope />, 
    description: 'تجربة في الخلايا العصبية والإشارات العصبية',
    iframe: 'https://phet.colorado.edu/sims/html/neuron/latest/neuron_ar_SA.html'
  },
  'color-vision-bio-lab': {
    title: 'رؤية الألوان',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaEye />, 
    description: 'تجربة في آلية رؤية الألوان في العين',
    iframe: 'https://phet.colorado.edu/sims/html/color-vision/latest/color-vision_ar_SA.html'
  },
  'ph-scale-bio-lab': {
    title: 'مقياس pH',
    subject: 'الأحياء',
    subjectColor: '#27ae60',
    icon: <FaFlask />, 
    description: 'تجربة في مقياس الرقم الهيدروجيني',
    iframe: 'https://phet.colorado.edu/sims/html/ph-scale/latest/ph-scale_ar_SA.html'
  },
  // معامل علم الأرض
  'greenhouse-effect-lab': {
    title: 'ظاهرة البيت الزجاجي',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaGlobe />, 
    description: 'تجربة في تأثير البيت الزجاجي على المناخ',
    iframe: 'https://phet.colorado.edu/sims/html/greenhouse-effect/latest/greenhouse-effect_ar_SA.html'
  },
  'density-earth-lab': {
    title: 'الكثافة',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaFlask />, 
    description: 'تجربة في قياس الكثافة والكتلة والحجم',
    iframe: 'https://phet.colorado.edu/sims/html/density/latest/density_ar_SA.html'
  },
  'gravity-force-lab-basics-lab': {
    title: 'مختبر قوة التجاذب',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCompass />, 
    description: 'تجربة أساسية في قوى الجاذبية',
    iframe: 'https://phet.colorado.edu/sims/html/gravity-force-lab-basics/latest/gravity-force-lab-basics_ar_SA.html'
  },
  'waves-intro-lab': {
    title: 'مقدمة الموجات',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCircle />, 
    description: 'تجربة في أساسيات الموجات وخصائصها',
    iframe: 'https://phet.colorado.edu/sims/html/waves-intro/latest/waves-intro_ar_SA.html'
  },
  'diffusion-earth-lab': {
    title: 'الانتشار',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaFlask />, 
    description: 'تجربة في عملية الانتشار الجزيئي',
    iframe: 'https://phet.colorado.edu/sims/html/diffusion/latest/diffusion_ar_SA.html'
  },
  'gas-properties-earth-lab': {
    title: 'خصائص الغاز',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaFlask />, 
    description: 'تجربة في خصائص الغازات وقوانينها',
    iframe: 'https://phet.colorado.edu/sims/html/gas-properties/latest/gas-properties_ar_SA.html'
  },
  'blackbody-spectrum-earth-lab': {
    title: 'إشعاع الجسم الأسود',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaAtom />, 
    description: 'تجربة في إشعاع الجسم الأسود والطيف',
    iframe: 'https://phet.colorado.edu/sims/html/blackbody-spectrum/latest/blackbody-spectrum_ar_SA.html'
  },
  'wave-interference-lab': {
    title: 'تداخل الموجات',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCircle />, 
    description: 'تجربة في تداخل الموجات والحيود',
    iframe: 'https://phet.colorado.edu/sims/html/wave-interference/latest/wave-interference_ar_SA.html'
  },
  'gravity-and-orbits-earth-lab': {
    title: 'الجاذبية والمدارات',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCompass />, 
    description: 'تجربة في الجاذبية والمدارات الكوكبية',
    iframe: 'https://phet.colorado.edu/sims/html/gravity-and-orbits/latest/gravity-and-orbits_ar_SA.html'
  },
  'wave-on-a-string-earth-lab': {
    title: 'موجة على وتر',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCircle />, 
    description: 'تجربة في الموجات الميكانيكية على الأوتار',
    iframe: 'https://phet.colorado.edu/sims/html/wave-on-a-string/latest/wave-on-a-string_ar_SA.html'
  },
  'ph-scale-earth-lab': {
    title: 'مقياس pH',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaFlask />, 
    description: 'تجربة في مقياس الرقم الهيدروجيني',
    iframe: 'https://phet.colorado.edu/sims/html/ph-scale/latest/ph-scale_ar_SA.html'
  },
  'under-pressure-earth-lab': {
    title: 'الضغط',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaTachometerAlt />, 
    description: 'تجربة في الضغط وتأثيره على المادة',
    iframe: 'https://phet.colorado.edu/sims/html/under-pressure/latest/under-pressure_ar_SA.html'
  },
  'gravity-force-lab-earth-lab': {
    title: 'معمل قوة الجذب',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaCompass />, 
    description: 'تجربة في قوى الجذب بين الأجسام',
    iframe: 'https://phet.colorado.edu/sims/html/gravity-force-lab/latest/gravity-force-lab_ar_SA.html'
  },
  'balloons-and-static-electricity-earth-lab': {
    title: 'البالونات والكهرباء الساكنة',
    subject: 'علم الأرض',
    subjectColor: '#f39c12',
    icon: <FaBolt />, 
    description: 'تجربة في الكهرباء الساكنة والبالونات',
    iframe: 'https://phet.colorado.edu/sims/html/balloons-and-static-electricity/latest/balloons-and-static-electricity_ar_SA.html'
  }
};

const LabDetails = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const iframeRef = useRef(null);
useEffect(() => {
  window.scrollTo(0, 0);
}, []);
  const labData = labsData[labId];
  if (!labData) return null;

  const handleBack = () => navigate('/labs');
  const handleFullscreen = () => {
    if (iframeRef.current) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if (iframeRef.current.webkitRequestFullscreen) {
        iframeRef.current.webkitRequestFullscreen();
      } else if (iframeRef.current.msRequestFullscreen) {
        iframeRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <div className={`lab-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Header Section */}
      <div className="lab-header">
        <div className="container">
          <div className="lab-header-content">
            <div className="lab-breadcrumb">
              <button className="breadcrumb-back-btn" onClick={handleBack}>
                <FaArrowLeft />
                <span>العودة للمعامل</span>
              </button>
              <span className="breadcrumb-link" onClick={handleBack}>المعامل</span>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{labData.title}</span>
            </div>
            <h1 className="lab-title">{labData.title}</h1>
            <p className="lab-description">{labData.description}</p>
          </div>
        </div>
      </div>
                  
      {/* Lab Content */}
      <div className="lab-content">
        <div className="container">
          <div className="lab-grid">
            {/* Lab Info Card */}
            <div className="lab-info-card">
              <div className="lab-info-header">
                <div className="lab-icon">{labData.icon}</div>
                <h3>معلومات المختبر</h3>
                  </div>
              <div className="lab-info-content">
                <div className="info-item">
                  <span className="info-label">المادة:</span>
                  <span className="info-value">{labData.subject}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">المستوى:</span>
                  <span className="info-value">{defaultLevel}</span>
            </div>
                <div className="info-item">
                  <span className="info-label">الوقت المقترح:</span>
                  <span className="info-value">{defaultTime}</span>
          </div>
                <div className="info-item">
                  <span className="info-label">المفاهيم:</span>
                  <span className="info-value">{defaultConcepts[labId] || 'تجربة تفاعلية'}</span>
                  </div>
                  </div>
                </div>

            {/* Lab Simulation */}
            <div className={`lab-simulation-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
              <div className="simulation-header">
                <h3>المحاكاة التفاعلية</h3>
                <div className="simulation-controls">
                  <button className="btn btn-secondary btn-sm" onClick={handleBack}>
                    <FaArrowLeft />
                    العودة للمعامل
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleFullscreen}>
                    <i className="fas fa-expand"></i>
                    ملء الشاشة
                  </button>
                </div>
              </div>
              <div className="simulation-container">
                <iframe
                  ref={iframeRef}
                  src={labData.iframe}
                  width="100%"
                  height="600"
                  allowFullScreen
                  title={labData.title}
                  className="lab-iframe"
                />
                  </div>
                </div>

            {/* Instructions Card */}
            <div className="lab-instructions-card">
              <div className="instructions-header">
                <h3>تعليمات المختبر</h3>
              </div>
              <div className="instructions-content">
                {defaultInstructions.map((step, idx) => (
                  <div className="instruction-step" key={idx}>
                    <div className="step-number">{idx + 1}</div>
                    <div className="step-content">
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default LabDetails; 